import * as Sentry from '@sentry/vue'

let sentryLoggerInstance = null
let activeFlow = null
let activeFlowEndTimer = null

export const ErrorCategory = {
  UI_ERROR: 'UI_ERROR',
  USER_ERROR: 'USER_ERROR',
  UNHANDLED_EXCEPTION: 'UNHANDLED_EXCEPTION',
  NETWORK_CONNECTIVITY: 'NETWORK_CONNECTIVITY',
  API_OUTAGE: 'API_OUTAGE',
  API_TIMEOUT: 'API_TIMEOUT',
  BUSINESS_RULE_FAILURE: 'BUSINESS_RULE_FAILURE',
  MISCONFIGURATION: 'MISCONFIGURATION',
  LIMIT_VIOLATION: 'LIMIT_VIOLATION',
  DEVICE_ERROR: 'DEVICE_ERROR',
  PRINTER_ERROR: 'PRINTER_ERROR',
}

export const ErrorReason = {
  INVALID_INPUT_FORMAT: 'INVALID_INPUT_FORMAT',
  EMPTY_REQUIRED_FIELD: 'EMPTY_REQUIRED_FIELD',

  HTTP_500: 'HTTP_500',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  SOCKET_TIMEOUT: 'SOCKET_TIMEOUT',
  GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',

  WRONG_PIN: 'WRONG_PIN',
  CARD_BLOCKED: 'CARD_BLOCKED',
  CARD_NOT_ACTIVATED: 'CARD_NOT_ACTIVATED',

  PROHIBITED_PRODUCT: 'PROHIBITED_PRODUCT',
  PROHIBITED_STATION: 'PROHIBITED_STATION',
  STATION_PRICE_NOT_SET: 'STATION_PRICE_NOT_SET',

  AMOUNT_EXCEEDS_LIMIT: 'AMOUNT_EXCEEDS_LIMIT',
  DAILY_LIMIT_EXCEEDED: 'DAILY_LIMIT_EXCEEDED',

  NULL_POINTER: 'NULL_POINTER',
  JSON_PARSE_ERROR: 'JSON_PARSE_ERROR',
  UNHANDLED_EXCEPTION: 'UNHANDLED_EXCEPTION',
}

export const ResponsibleTeam = {
  CUSTOMER_SUPPORT: 'CUSTOMER_SUPPORT',
  USER: 'USER',
  APP: 'APP',
  SWITCH: 'SWITCH',
  INFRASTRUCTURE: 'INFRASTRUCTURE',
  API: 'API',
}

export const getDefaultTags = () => ({
  application_name: 'CUSTOMER_PORTAL',
  platform: import.meta.env.VITE_APP_SOURCE || 'WEB',
  institution_name: import.meta.env.VITE_APP_INSTID || 'GALANAMG',
  institution_id: import.meta.env.VITE_APP_INSTID || 'GALANAMG',
  journey_name: 'customer_portal',
  environment: import.meta.env.VITE_APP_ENVIRONMENT || 'dev',
  app_version: import.meta.env.VITE_RELEASE_VERSION || 'unknown',
  release_tag_date: import.meta.env.VITE_RELEASE_DATE || 'unknown',
  app_source: 'frontend',
  app_module: '',
  device_type: 'web',
  country_code: import.meta.env.VITE_APP_DEFAULT_COUNTRY || 'unknown',
  locale: localStorage.getItem('lang') || 'en',
})

export function initSentryLogger(logger) {
  if (!sentryLoggerInstance) {
    sentryLoggerInstance = logger
  }
}

export function getSentryService() {
  if (!sentryLoggerInstance) {
    console.warn('Sentry not initialized')
    return null
  }

  const startSpan = ({ name, op = 'custom', attributes = {}, parentSpan }) => {
    const span = Sentry.startInactiveSpan({
      name,
      op,
      parentSpan: parentSpan || activeFlow,
    })

    span?.setAttributes?.({
      ...getDefaultTags(),
      ...attributes,
    })

    return span
  }

  const endSpan = (span, extra = {}) => {
    try {
      if (!span) return

      span?.setAttributes?.(extra)
      span?.end?.()

      if (span === activeFlow) {
        activeFlow = null
      }
    } catch (err) {
      console.warn('[Sentry] endSpan failed:', err)
    }
  }

  const startTransaction = (name, attributes = {}) => {
    return startSpan({
      name,
      op: 'transaction',
      attributes: {
        transaction: true,
        ...attributes,
      },
    })
  }

  const startFlow = (name, attributes = {}) => {
    if (activeFlowEndTimer) {
      clearTimeout(activeFlowEndTimer)
      activeFlowEndTimer = null
    }

    if (activeFlow) {
      endSpan(activeFlow, {
        flow_replaced: true,
      })
    }

    activeFlow = startTransaction(name, {
      journey_type: 'ui_flow',
      ...attributes,
    })

    return activeFlow
  }

  const addFlowStep = (stepName, attributes = {}) => {
    if (!activeFlow) return null

    const stepSpan = startSpan({
      name: stepName,
      op: 'ui.step',
      attributes,
      parentSpan: activeFlow,
    })

    setTimeout(() => endSpan(stepSpan), 50)

    return stepSpan
  }

  const endFlow = (attributes = {}) => {
    if (!activeFlow) return

    const flowToEnd = activeFlow
    activeFlow = null

    flowToEnd?.setAttributes?.({
      flow_completed: true,
      ...attributes,
    })

    activeFlowEndTimer = setTimeout(() => {
      endSpan(flowToEnd)
      activeFlowEndTimer = null
    }, 75)
  }
  const reportClassifiedError = ({
    error,
    error_category,
    error_reason,
    responsible_team,
    journey_stage,
    endpoint,
    payload,
    description,
    user_action,
    level = 'error',
  }) => {
    sentryLoggerInstance.logApplicationError(
      error,
      {
        action: journey_stage,
        handled: true,
        level,
      },
      {
        tags: {
          error_category,
          error_reason,
          responsible_team,
          api_end_point_request_type: endpoint,
        },
        extra: {
          payload,
          description: description,
          user_action: user_action,
        },
      },
    )
  }

  return {
    trackApiCall: (apiCall, options = {}) =>
      sentryLoggerInstance.logApiCall(apiCall, options),

    logError: (error, context, extra) =>
      sentryLoggerInstance.logApplicationError(error, context, extra),

    logApiFailure: (apiFailure, options = {}) =>
      sentryLoggerInstance.logApiFailure(apiFailure, options),

    addBreadcrumb: (msg, category, level) =>
      sentryLoggerInstance.addBreadcrumb(msg, category, level),

    startSpan,
    endSpan,
    startTransaction,

    startFlow,
    addFlowStep,
    endFlow,

    setUserContext: (user) => Sentry.setUser(user),
    reportClassifiedError,
    instance: sentryLoggerInstance,
  }
}
